import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import PreviewImage from '../PreviewImage';
import PackageMockup from './PackageMockup';
import {
  getAppropriateBg,
  getAppropriateForeground,
} from '../_pages/_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import usePackagePoses from '../../hooks/usePackagePoses';
import usePricelist from '../../hooks/usePricelist';
import useImageOrientation, { ORIENTATION } from '../../hooks/useImageOrientation';
import { cameraLetterOf, poseNumberOf } from '../CustomerCart/cameraSort';
import {
  CapIcon, ChevronIcon, FavoriteIcon, HeartIcon,
} from './icons';

const readFavorites = (key) => {
  if (!key) return new Set();
  try {
    const raw = window.sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (_) {
    return new Set();
  }
};

/**
 * The senior flow's pose card, kept exactly as it is on `main`.
 *
 * The elementary gallery was redesigned on this branch (photo edge to edge in its true
 * 4:5 / 5:4 print ratio, controls in a caption row under the picture); the senior
 * favorites screen is explicitly NOT part of that and must render exactly as it does in
 * production. Rather than branch inside one card on half a dozen classes, the two designs
 * are two components and PortraitsGallery picks by audience. Delete this one the day
 * seniors adopt the new design.
 */
function SeniorPoseCard({
  pose, backgroundUrl, isYb, pending, isFav, ineligible, step, yearbook,
  onZoom, onSelectYearbook, onToggleFavorite,
}) {
  const { orientation } = useImageOrientation(pose.watermarkUrl);
  const isLandscape = orientation === ORIENTATION.LANDSCAPE;
  const cameraLetter = cameraLetterOf(pose);

  return (
    <div
      className={cn(
        // White matte with a consistent gap between the photo and the frame. The frame IS the
        // selection state: a subtle gray until picked, pink once favorited, blue for the
        // yearbook pose. Ring (not border) so the frame never shifts the layout.
        'relative rounded-2xl bg-white p-1.5 shadow-sm',
        isLandscape && 'col-span-2',
        // eslint-disable-next-line no-nested-ternary
        (isYb || pending) ? 'ring-2 ring-shoob-blue' : isFav ? 'ring-2 ring-pink-500' : 'ring-1 ring-gray-200',
      )}
    >
      {/* Inner panel holds the photo AND every overlay, so the badges/buttons hug the image
          edge rather than floating in the matte. overflow-hidden + rounded clips to the panel. */}
      <div className={cn('relative overflow-hidden rounded-xl bg-gray-100', isLandscape ? 'aspect-[4/3]' : 'aspect-[3/4]')}>
        {/* PreviewImage layers the background at z-index:-1 and relies on its container being a
            stacking context (position:relative + z-index). Round the images themselves too, so
            a composited child is clipped to the rounded panel on every engine. */}
        <button
          type="button"
          onClick={onZoom}
          aria-label={`Enlarge photo ${poseNumberOf(pose)}`}
          className="absolute inset-0 z-[1] cursor-zoom-in rounded-xl overflow-hidden"
        >
          <PreviewImage
            foregroundUrl={pose.watermarkUrl}
            backgroundUrl={backgroundUrl}
            containerClass="relative z-[1] w-full h-full overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:object-cover [&_img]:rounded-xl"
          />
        </button>

        {/* Yearbook toggle — blocked, not hidden, on an ineligible pose. */}
        {yearbook && (
          <button
            type="button"
            onClick={onSelectYearbook}
            disabled={pending}
            title={ineligible ? yearbook.note : undefined}
            aria-label={isYb ? 'Yearbook pose' : 'Choose as yearbook pose'}
            className={cn(
              'absolute top-2 right-2 z-10 h-8 w-8 rounded-full flex items-center justify-center shadow',
              (isYb || pending) ? 'bg-shoob-blue text-white' : 'bg-black/60 text-white hover:bg-black/80',
              pending && 'animate-pulse',
              ineligible && 'opacity-40 cursor-not-allowed',
            )}
          >
            <CapIcon className="h-4 w-4" />
          </button>
        )}

        {/* Frame ID — pose number + camera letter, e.g. "9F". A session is often split
            across cameras, so the pose number alone doesn't identify a frame; this is
            how staff and customers refer to one on the phone. */}
        <span
          className="absolute top-2 left-2 z-10 rounded bg-black/60 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white"
          title={`Pose ${poseNumberOf(pose)}${cameraLetter ? `, Camera ${cameraLetter}` : ''}`}
        >
          {`${poseNumberOf(pose)}${cameraLetter}`}
        </span>

        {/* Why the yearbook button is dead on this card. Bottom-left, clear of the frame ID. */}
        {ineligible && step === 1 && (
          <span className="absolute bottom-2 left-2 z-10 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white">
            Not yearbook eligible
          </span>
        )}

        <button
          type="button"
          onClick={onToggleFavorite}
          aria-label={isFav ? 'Remove favorite' : 'Add favorite'}
          className={cn(
            'absolute bottom-2 right-2 z-10 h-9 w-9 rounded-full flex items-center justify-center shadow',
            isFav ? 'bg-pink-500 text-white' : 'bg-black/40 text-white hover:bg-black/60',
          )}
        >
          <HeartIcon filled={isFav} className="h-4 w-4" />
        </button>

        {isYb && (
          <div className="absolute bottom-0 inset-x-0 z-10 bg-shoob-blue text-white text-center text-sm font-semibold py-1.5 flex items-center justify-center gap-1">
            <CapIcon className="h-4 w-4" />
            {' '}
            YEARBOOK
          </div>
        )}
      </div>
    </div>
  );
}

SeniorPoseCard.defaultProps = {
  backgroundUrl: null,
  yearbook: null,
  ineligible: false,
  pending: false,
};

SeniorPoseCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  pose: PropTypes.object.isRequired,
  backgroundUrl: PropTypes.string,
  isYb: PropTypes.bool.isRequired,
  pending: PropTypes.bool,
  isFav: PropTypes.bool.isRequired,
  ineligible: PropTypes.bool,
  step: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  yearbook: PropTypes.object,
  onZoom: PropTypes.func.isRequired,
  onSelectYearbook: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
};

// The card's caption row: the strip under the photo carrying the frame ID and the heart.
//
// A FIXED height rather than padding around whatever sits inside it. h-12 is 48px — the 44px
// touch target plus 2px of air top and bottom, which is exactly what `py-0.5` around an h-11
// button produced before. Fixed, because the PROMO tile renders this row EMPTY so it stands the
// same height as the pose cards beside it: with padding alone an empty row collapses to 4px and
// the promo rides 44px short of everything else.
//
// Shared, so the two can never drift. Change the height here and both move together.
const CAPTION_ROW = 'flex h-12 items-center justify-between gap-2 px-1.5';

/**
 * One pose card. Split out from the grid so it can detect its own orientation with a hook
 * (hooks can't run inside the .map that renders the grid).
 *
 * Landscape poses — memory mates, "Class of 20XX" graphics, group shots — must NOT be
 * cover-cropped into a portrait frame: that zooms in and slices off the sides (and any
 * baked-in graphic text). The old favorites screen handled this by spanning a landscape
 * pose across two columns (favorite-pose-item's `.big`); we do the same here. A landscape
 * pose gets a 5:4 frame that spans two grid columns; a portrait pose keeps the single-column
 * 4:5 frame. Both are the ratio the print is sold at. Orientation resolves async from the
 * image, so a pose renders
 * portrait for one frame and re-flows once its dimensions are known — same as the old screen.
 */
function PoseCard({
  pose, backgroundUrl, isYb, pending, isFav, ineligible, step, yearbook,
  onZoom, onSelectYearbook, onToggleFavorite,
}) {
  const { orientation } = useImageOrientation(pose.watermarkUrl);
  const isLandscape = orientation === ORIENTATION.LANDSCAPE;
  const cameraLetter = cameraLetterOf(pose);

  return (
    <div
      className={cn(
        // Square corners, no matte: the photo IS the card, edge to edge. Favoriting shows
        // ONLY in the caption row's heart -- no stroke around the picture. The yearbook
        // pick keeps its blue ring: that one is a single required choice among all the
        // poses, so it has to be findable by scanning the grid rather than reading each
        // card. Ring (not border) so it never shifts the layout.
        //
        // A white card on a near-white page needs the shadow to separate it from the sheet,
        // and shadow-md at 10% black was still washing out against the light background —
        // hence the custom, darker cast. The photo keeps square top corners (it runs edge to
        // edge into the card) while the bottom corners round with the caption row, so the
        // card reads as one object that ends deliberately rather than being cut off.
        'relative bg-white rounded-b-lg shadow-[0_4px_10px_rgba(0,0,0,0.18)]',
        isLandscape && 'col-span-2',
        (isYb || pending) && 'ring-2 ring-shoob-blue',
      )}
    >
      {/* The frame is the print ratio the photo is actually shot and sold at -- 4:5 upright,
          5:4 on its side -- so nothing is letterboxed into grey bars or cropped to fit a
          frame of a different shape. Holds the photo AND every overlay, so the badges and
          buttons hug the image edge. */}
      <div className={cn('relative overflow-hidden bg-white', isLandscape ? 'aspect-[5/4]' : 'aspect-[4/5]')}>
        {/* PreviewImage layers the background at z-index:-1 and relies on its container being a
            stacking context (position:relative + z-index). object-cover fills the frame; now
            that the frame is the photo's own ratio there is nothing left to crop. */}
        {/* p-0 and bg-transparent are load-bearing, not tidiness. Tailwind's preflight is OFF
            (to coexist with Bootstrap), so a bare <button> keeps its USER-AGENT padding of
            1px 6px and its `buttonface` background — measured as rgb(239,239,239). That put
            a 6px light-grey strip down the left and right of every photo, because the
            PreviewImage inside was inset by the button's own padding (155px wide inside a
            167px frame). The root's [&_button]:border-0 only ever reset the border.

            Do NOT "fix" this globally with [&_button]:p-0 on the root: a descendant variant
            is specificity (0,1,1) and would out-rank the padding utilities on every padded
            button in this gallery. It belongs here, on the button that wants none. */}
        <button
          type="button"
          onClick={onZoom}
          aria-label={`Enlarge photo ${poseNumberOf(pose)}`}
          className="absolute inset-0 z-[1] p-0 bg-transparent cursor-zoom-in overflow-hidden"
        >
          <PreviewImage
            foregroundUrl={pose.watermarkUrl}
            backgroundUrl={backgroundUrl}
            containerClass="relative z-[1] w-full h-full overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:object-cover"
          />
        </button>

        {/* Yearbook toggle — blocked, not hidden, on an ineligible pose. */}
        {yearbook && (
          <button
            type="button"
            onClick={onSelectYearbook}
            disabled={pending}
            title={ineligible ? yearbook.note : undefined}
            aria-label={isYb ? 'Yearbook pose' : 'Choose as yearbook pose'}
            className={cn(
              'absolute top-2 right-2 z-10 h-11 w-11 rounded-full flex items-center justify-center shadow',
              (isYb || pending) ? 'bg-shoob-blue text-white' : 'bg-black/60 text-white hover:bg-black/80',
              pending && 'animate-pulse',
              ineligible && 'opacity-40 cursor-not-allowed',
            )}
          >
            <CapIcon className="h-4 w-4" />
          </button>
        )}

        {/* Why the yearbook button is dead on this card. Bottom-left, clear of the frame ID. */}
        {ineligible && step === 1 && (
          <span className="absolute bottom-2 left-2 z-10 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-medium text-white">
            Not yearbook eligible
          </span>
        )}

        {isYb && (
          <div className="absolute bottom-0 inset-x-0 z-10 bg-shoob-blue text-white text-center text-sm font-semibold py-1.5 flex items-center justify-center gap-1">
            <CapIcon className="h-4 w-4" />
            {' '}
            YEARBOOK
          </div>
        )}
      </div>

      {/* Caption row. The frame ID and the two actions used to float on top of the photo,
          which meant every card had furniture sitting over the face. They live here now, on
          the card itself, below the picture — nothing covers the portrait.

          The buttons are 44x44 (h-11 w-11) — the Apple HIG / WCAG 2.5.5 minimum touch
          target — while the glyphs inside stay 18px. The row's own padding is dialled back
          to compensate, so honouring the target costs 4px of card height, not 12. */}
      <div className={CAPTION_ROW}>
        {/* Frame ID — pose number + camera letter, e.g. "9F". A session is often split
            across cameras, so the pose number alone doesn't identify a frame; this is
            how staff and customers refer to one on the phone. */}
        <span
          className="text-xs font-semibold tabular-nums text-gray-400"
          title={`Pose ${poseNumberOf(pose)}${cameraLetter ? `, Camera ${cameraLetter}` : ''}`}
        >
          {`${poseNumberOf(pose)}${cameraLetter}`}
        </span>

        {/* The heart is the only action in the row. The magnifier that used to sit beside
            it is gone — tapping the photo already enlarges it, so the icon was a second
            control for a thing the picture itself does, and it made the caption row look
            like a toolbar. The photo keeps its `Enlarge photo` label for screen readers. */}
        <button
          type="button"
          onClick={onToggleFavorite}
          aria-label={isFav ? 'Remove favorite' : 'Add favorite'}
          className={cn(
            'h-11 w-11 rounded-full flex items-center justify-center bg-transparent',
            isFav ? 'text-pink-500' : 'text-gray-400 hover:text-pink-500',
          )}
        >
          {/* 22px glyph in a 44px target. The heart is now the only control in the row, so
              it can afford the weight — at 18px it read as a leftover next to the magnifier
              that used to sit beside it. */}
          <FavoriteIcon filled={isFav} className="h-[22px] w-[22px]" />
        </button>
      </div>
    </div>
  );
}

PoseCard.defaultProps = {
  backgroundUrl: null,
  yearbook: null,
  ineligible: false,
  pending: false,
};

PoseCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  pose: PropTypes.object.isRequired,
  backgroundUrl: PropTypes.string,
  isYb: PropTypes.bool.isRequired,
  pending: PropTypes.bool,
  isFav: PropTypes.bool.isRequired,
  ineligible: PropTypes.bool,
  step: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  yearbook: PropTypes.object,
  onZoom: PropTypes.func.isRequired,
  onSelectYearbook: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
};

/**
 * The photo gallery every audience picks their favorites in (Figma v3).
 *
 * ONE implementation, parameterized — NOT a senior screen with an elementary copy beside
 * it. The audiences differ only in capabilities:
 *
 *   seniors     -> `yearbook` capability supplied (step 1 picks the official yearbook
 *                  pose, garment-gated), plus `backdropUrl` (the school's senior backdrop,
 *                  since seniors are shot as cutouts).
 *   everyone    -> no `yearbook` prop: favorites only, backgrounds per the pricelist's
 *                  package-poses spec. The step banner collapses to a single line.
 *
 * The caller owns what is audience-specific (how a yearbook pose is SAVED, what the
 * backdrop is, the headline); this component owns what is universal (the grid, favorites,
 * ordering, frame IDs, the continue bar). Add a capability prop before you add a copy.
 */
export default function PortraitsGallery({
  poses,
  backgrounds,
  packageId,
  headline,
  subhead,
  backdropUrl,
  yearbook,
  favoritesKey,
  promo,
  onContinue,
  continueLabel,
  senior,
}) {
  const { data: packagePoses } = usePackagePoses(packageId);
  const { data: pricelist } = usePricelist(packageId);

  const [favorites, setFavorites] = useState(() => readFavorites(favoritesKey));

  const yearbookId = yearbook?.selectedPoseId || null;
  // Two-step only when a yearbook pose is being chosen; otherwise it's favorites, one step.
  const step = yearbook && !yearbookId ? 1 : 2;

  // Fail-open: a pose with no verdict is pickable, so a classifier outage never blocks a
  // customer. Without the yearbook capability nothing is ever ineligible.
  const isEligible = (pose) => !yearbook || yearbook.isEligible(pose);

  // Yearbook-eligible poses first, the rest after — a STABLE partition (Array#filter),
  // so within each group the photographer's favorite order (the API's display_order,
  // then number) survives untouched. Ineligible poses are shown, never hidden: this is
  // also the purchase surface, so a cap-and-gown photo stays heartable and buyable —
  // only the yearbook action is gated. Same order in both steps, so nothing reshuffles
  // under the customer's finger once they pick.
  const orderedPoses = useMemo(() => {
    const list = (poses || []).filter(Boolean);
    if (!yearbook) return list;
    // Sort into bands, STABLE within each (the index tiebreaker preserves the API's
    // display_order/number). The senior wrapper supplies a 3-way key (yearbook-formal ->
    // cap & gown -> other); any other audience falls back to eligible-first / rest-after.
    const bandOf = yearbook.bandOf || ((pose) => (yearbook.isEligible(pose) ? 0 : 1));
    return list
      .map((pose, i) => ({ pose, i, band: bandOf(pose) }))
      .sort((a, b) => a.band - b.band || a.i - b.i)
      .map((entry) => entry.pose);
  }, [poses, yearbook]);

  // Set when Continue is pressed with nothing chosen. A DISABLED button fires no click, so
  // the old dead-grey Continue could not explain itself — a parent who did not realise the
  // hearts were the point just pressed it and got silence. It is pressable now and answers.
  const [continueHint, setContinueHint] = useState(false);

  const persistFavorites = (nextSet) => {
    setFavorites(nextSet);
    // Answered — take the hint away the moment they pick something.
    if (nextSet.size > 0) setContinueHint(false);
    if (favoritesKey) {
      try {
        window.sessionStorage.setItem(favoritesKey, JSON.stringify([...nextSet]));
      } catch (_) { /* private browsing — favorites just won't survive a reload */ }
    }
  };

  const toggleFavorite = (id) => {
    const next = new Set(favorites);
    if (next.has(id)) next.delete(id); else next.add(id);
    persistFavorites(next);
  };

  const favoriteAll = () => persistFavorites(new Set((poses || []).map((pose) => pose.id)));
  const clearFavorites = () => persistFavorites(new Set());

  // Click-to-enlarge. Parents want a proper look at a photo before hearting it, and the
  // decision they're making here (which photos to buy) is hard to make off a thumbnail.
  //
  // Held as an ID, not as an index or the pose object: `orderedPoses` re-sorts the moment
  // a yearbook pose is picked, so a stored index would silently start pointing at a
  // different photo while the customer is looking at it.
  const [zoomedPoseId, setZoomedPoseId] = useState(null);
  const zoomedIndex = zoomedPoseId === null
    ? -1
    : orderedPoses.findIndex((pose) => pose.id === zoomedPoseId);
  const zoomedPose = zoomedIndex === -1 ? null : orderedPoses[zoomedIndex];

  // Pager for the enlarged view — the whole point of enlarging is to compare photos, and
  // closing back to the grid between every pair made that a chore. Walks `orderedPoses`,
  // so the arrows follow the same order the grid shows.
  //
  // Clamped, not wrapped: reaching the last photo should read as "that's all of them",
  // which a dead arrow says and a silent jump back to the first one does not.
  const stepZoom = (delta) => {
    if (zoomedIndex === -1) return;
    const next = zoomedIndex + delta;
    if (next < 0 || next >= orderedPoses.length) return;
    setZoomedPoseId(orderedPoses[next].id);
  };

  const hasPrev = zoomedIndex > 0;
  const hasNext = zoomedIndex !== -1 && zoomedIndex < orderedPoses.length - 1;

  // The enlarged frame takes the PHOTO's own shape rather than a fixed 4:5, so a landscape
  // pose (memory mate, "Class of 20XX", group shot) opens as a landscape card instead of
  // being letterboxed into an upright one with dead white space above and below it.
  //
  // Same hook the cards use, so by the time a photo can be tapped its dimensions are already
  // in the shared cache and this resolves without a second network round trip. Until it does
  // resolve (or if the image never loads) there is no ratio, and the frame falls back to the
  // 4:5 it always used — the common case, since most poses are upright.
  const { width: zoomWidth, height: zoomHeight } = useImageOrientation(zoomedPose?.watermarkUrl);
  const zoomRatio = zoomWidth && zoomHeight ? `${zoomWidth} / ${zoomHeight}` : null;

  // Pager arrows. One class string, applied to both, so they cannot drift apart.
  //
  // They live BELOW the card now rather than floating on the photo — no absolute positioning
  // — because an arrow drawn over a portrait reads as part of the portrait no matter how
  // transparent it is. Kept translucent + blurred + shadowed anyway: on the dim backdrop
  // that reads as a control surface, and the hairline ring keeps the edge findable.
  const arrowClass = 'h-11 w-11 rounded-full flex items-center justify-center bg-white/50 '
    + 'backdrop-blur-sm text-gray-900 shadow-lg ring-1 ring-black/10';

  // The pose that fills the header. Pose 1 by number — the frame the photographer shoots
  // first, and the one most likely to be the straight-ahead portrait — falling back to
  // whatever leads the grid when there is no pose numbered 1 (a session split across
  // cameras can start at 13). Unfiltered `poses`, not orderedPoses: the yearbook banding
  // reshuffles that list, and the header must not change photo when a pose is picked.
  const bannerPose = useMemo(() => {
    const list = (poses || []).filter(Boolean);
    return list.find((pose) => poseNumberOf(pose) === 1) || list[0] || null;
  }, [poses]);

  // The header's package mockup fills its sheets from the customer's OWN favorites, in the
  // order the grid shows them, so hearting a photo puts it on the 8x10 — the header becomes
  // the package being built rather than a fixed decoration.
  //
  // Nothing is favorited on arrival (picking them is the point of the screen), so both slots
  // fall back to bannerPose. `secondary` then falls back to `primary` rather than going
  // empty: a real package is routinely one pose at several sizes, so repeating it looks
  // right, and it costs no extra network — same URL, one fetch, however many sheets draw it.
  const packageSheets = useMemo(() => {
    const chosen = orderedPoses.filter((pose) => favorites.has(pose.id));
    const bgFor = (pose) => getAppropriateBg(packagePoses, pose, backgrounds, pricelist)?.imageUrl;

    // The two backdrops to alternate are the first two the PAGE ITSELF shows — i.e. the ones
    // on the leading cards in the grid — not `backgrounds[0..1]`.
    //
    // That distinction is the whole bug this replaced: `backgrounds` is the package's full
    // available list, and the grid's variety does not come from its order. It comes from
    // getAppropriateBg, which maps each pose NUMBER to the backdrop the pricelist's
    // package-poses spec assigns it. Indexing the available list therefore handed every sheet
    // the same first entry, and the fan rendered monotone while the cards below it were
    // green / orange / blue.
    //
    // DISTINCT, and in grid order: two sheets showing the same backdrop is not an
    // alternation, so if the leading poses happen to share one this keeps looking until it
    // finds a second — the point is that both are visibly different.
    const palette = [];
    orderedPoses.forEach((pose) => {
      const url = bgFor(pose);
      if (url && palette.length < 2 && !palette.includes(url)) palette.push(url);
    });

    // FOUR sheets, in package order: the 8x10, the two 5x7s, then the wallet sheet. The wallet
    // block counts as ONE sheet — four wallets on a single piece of paper.
    //
    // Which FAVORITE each sheet shows. The 8x10 and the wallet sheet both take the first
    // favorite (it is the pose a parent leads with, and the one they hand out); the 5x7s take
    // the second and third. Repeats the first favorite when they have picked fewer, so the fan
    // is never gappy — and repeating costs no extra fetch, being the same URL.
    const poseForSheet = [0, 1, 2, 0];

    return poseForSheet.map((favoriteIndex, sheet) => {
      const pose = chosen[favoriteIndex] || chosen[0] || bannerPose;
      if (!pose) return null;

      return {
        // Clean pose, not the proof — same call the single-photo header made, for the same
        // reason (a PROOF stamp across a child's face is the wrong first impression) and with
        // the same fallback, since PaperclipSyncWorker rescues a missing watermark render.
        foregroundUrl: pose.imageUrl || pose.watermarkUrl,
        // The backdrop ALTERNATES across the sheets: 1st -> first backdrop, 2nd -> second,
        // 3rd -> first again, 4th -> second. So no two sheets in sequence share one and the
        // fan shows both without looking randomly assigned.
        //
        // Per SHEET, not per pose — which means both backdrops show even before anything is
        // favorited, so the header doubles as a preview of what is on offer.
        //
        // A page showing only one backdrop cannot alternate, so every sheet takes it; with
        // none resolvable at all it falls back to this pose's own background and then to the
        // package's first, rather than rendering a bare cutout on the navy.
        backgroundUrl: palette[sheet % 2] || palette[0] || bgFor(pose)
          || (backgrounds || [])[0]?.imageUrl,
      };
    });
  }, [orderedPoses, favorites, bannerPose, packagePoses, backgrounds, pricelist]);

  // `headline` is either a plain string (seniors, and anything that renders it flat) or
  // { lead, rest } — the emphasised part and the remainder. Split here rather than in the
  // markup so the two-weight branch below stays readable.
  const headlineLead = typeof headline === 'string' ? null : headline.lead;
  const headlineText = typeof headline === 'string' ? headline : headline.rest;

  const allFavorited = poses.length > 0 && favorites.size === poses.length;
  const hasFavorites = favorites.size > 0;

  // ONE bulk button with two states, rather than a Favorite All and a Clear competing beside
  // each other. It offers "Favorite All" until everything actually IS a favorite, then flips
  // to undoing that — so the button is never dead, and the row never shows an action that
  // would do nothing.
  //
  // The predicate is allFavorited, NOT hasFavorites: flipping to Clear after a single heart
  // would take "favorite the lot" away from a parent who had picked one photo and then
  // decided they wanted them all.
  const bulkAction = allFavorited
    ? { label: 'Clear Favorites', onClick: clearFavorites, filled: true }
    : { label: 'Favorite All', onClick: favoriteAll, filled: false };

  // Quiet secondary action — white pill, hairline grey edge. Shared by the header's
  // Favorite All and the sticky bar's Favorite All / Clear, so the three cannot drift.
  //
  // `ring-1 ring-gray-300`, NOT `border`: the root's [&_button]:border-0 is a descendant
  // selector (specificity 0,1,1) and beats a plain `border` utility (0,1,0), so a border
  // on any button in this gallery silently does not render. Ring also never shifts layout.
  const quietButtonClass = 'rounded-full font-semibold bg-white text-gray-900 ring-1 '
    + 'ring-gray-300 shadow-sm flex items-center justify-center gap-2 whitespace-nowrap';

  // Sizing for the sticky bar's buttons, shared so the row can never end up ragged.
  //
  // min-h-[56px] on phones: this bar is the one a parent uses one-handed, at the bottom of
  // the screen, often while holding a child. 44px is the Apple HIG / WCAG 2.5.5 floor and
  // these were only just clearing it (py-3 + a 20px line box). 56px is Material's comfortable
  // size for a primary action and gives the thumb real room. Desktop drops back to the
  // compact 44 — a mouse doesn't need the slack, and a 56px pill looks oversized up there.
  const stickyButtonSize = 'flex-1 sm:flex-none sm:w-auto min-h-[56px] sm:min-h-[44px] '
    + 'px-3 sm:px-6 py-3 text-base sm:text-base';

  useEffect(() => {
    if (!zoomedPose) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setZoomedPoseId(null);
      if (event.key === 'ArrowLeft') stepZoom(-1);
      if (event.key === 'ArrowRight') stepZoom(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomedPose, zoomedIndex, orderedPoses]);

  // The pose whose yearbook save is in flight. Drives an INSTANT optimistic ring + a
  // spinning cap so a single click visibly registers — the confirmed blue ring (isYb)
  // only lands after the server saves and the gallery refetches, which read as a dead
  // click otherwise (and the parent kept clicking).
  const [pendingYearbookId, setPendingYearbookId] = useState(null);

  const selectYearbook = async (pose) => {
    if (!yearbook) return;
    // Ineligible poses only surface the reason (onSelect alerts) — no optimistic ring.
    if (yearbook.isEligible && !yearbook.isEligible(pose)) {
      await yearbook.onSelect(pose);
      return;
    }
    setPendingYearbookId(pose.id);
    try {
      const saved = await yearbook.onSelect(pose);
      if (!saved) return;
      // The yearbook pose is a favorite by definition.
      const next = new Set(favorites);
      next.add(pose.id);
      persistFavorites(next);
    } finally {
      setPendingYearbookId(null);
    }
  };

  // (The banner no longer shows the yearbook pose number -- the blue-ringed card
  // identifies it; keep only the existence check.)

  // [&_button]:border-0 on the root resets the default browser button border on every button
  // in the gallery. Tailwind's preflight is OFF (to coexist with Bootstrap), so bare <button>s
  // keep their user-agent border — which draws a thin box around each photo card and the icon
  // buttons. One descendant variant clears them all.
  //
  // Deliberately NO font utility here. The gallery inherits 'AvenirNextLTPro-Regular' from
  // .customer-app (CustomerApp.sass), which is the whole customer app's font. A `font-sans`
  // on this root would silently swap the gallery to Tailwind's system stack and leave it the
  // one screen in the flow not wearing the brand face. Verified in Chrome: .customer-app's
  // rule beats the customer layout's reset.css on specificity (class > element), so the
  // inherited font reaches every node in here already.
  return (
    // Casual-save deterrence for every photo on the page, applied ONCE here rather than at
    // each of the four PreviewImage call sites (banner, card, senior card, enlarged view) —
    // a per-site copy is the kind of thing that gets added to three of them and forgotten on
    // the fourth the next time someone adds a surface.
    //
    // Be clear about what this buys: it closes right-click > Save Image and the iOS/Android
    // long-press "Save to Photos" sheet, which is how a photo actually walks off a page. It
    // is NOT protection. The presigned URLs are in the network tab, Save Page As still works,
    // and no CSS has ever stopped a screenshot. What genuinely limits the exposure is
    // resolution: these are `image.url('original')` / `watermark.url(:original)`, which
    // PaperclipSyncWorker fills from the PNGMED and WMPNG buckets (paperclip_sync_worker.rb
    // :57-72) — the 640x800 previews, not print files. Full-res lives at the separate
    // :full_res style that Api::PoseResource never serves to a customer.
    //
    // pointer-events-none on the img is the load-bearing part: both menus are built from the
    // event TARGET, so with the photo unable to be one, the browser has no image to offer
    // "Save Image As" for — the press lands on the wrapper instead. It costs nothing in
    // behaviour here because no img in this gallery carries its own handler: the card's photo
    // is a <button> AROUND the image (onZoom), and the click still reaches it.
    //
    // -webkit-touch-callout:none is what suppresses the iOS long-press sheet. It inherits, so
    // the root reaches every descendant. Written as an arbitrary property rather than an
    // inline style deliberately — jsdom silently drops the unknown declaration from
    // element.style, so an inline version cannot be asserted on in a test.
    <div
      className={'mx-auto max-w-6xl px-4 pb-28 [&_button]:border-0 [-webkit-touch-callout:none] '
        + '[&_img]:pointer-events-none [&_img]:select-none'}
      // Belt to that braces, and scoped to photo surfaces: `closest` walks UP from the press
      // to a PreviewImage container, which is what a right-click on any photo here actually
      // lands on (the img itself is pointer-events-none, so the hit falls through to its
      // container).
      //
      // Deliberately NOT `e.target.querySelector('img')` as well, which an earlier version
      // had: that matches any ANCESTOR of a photo — the grid wrapper and this very root
      // included — so a right-click on the page's own whitespace lost the browser's menu
      // (back, reload, print) while protecting nothing. Text is untouched either way, so a
      // parent can still select and copy a frame number.
      onContextMenu={(e) => {
        if (e.target.closest?.('.previewImage')) e.preventDefault();
      }}
    >
      {/* Title block.
          Seniors keep `main`'s white card and padding exactly. Everyone else gets a
          full-bleed banner built from the child's own first pose — see bannerPose. */}
      <div
        className={cn(
          senior
            ? 'text-center bg-white px-4 rounded-b-lg shadow-sm pt-5 pb-3 sm:py-8 flex flex-wrap sm:flex-nowrap items-center justify-center gap-x-2 gap-y-3 sm:gap-3 md:gap-4'
            // -mx-4 breaks the banner out of the gallery's own px-4 gutter so it runs edge
            // to edge. Always painted: the package sits ON the ground, so a slow photo
            // appears over a finished header rather than the header flashing bare.
            //
            // A warm ivory, replacing the deep navy this shipped with. White-bordered prints
            // read as prints laid on paper against it rather than cutouts pasted on a dark
            // panel, and the sheets' own shadows finally register — against a dark ground they
            // were muted, because the ground was already dark.
            //
            // The page below is WHITE and its cards are white, so the ivory is what separates
            // the header from the grid; a hairline bottom edge does the rest, since two warm
            // near-whites meeting need a line rather than a shadow to read as a boundary.
            //
            // `bg-gallery-warmth` deepens the ground toward the right, where the package sits —
            // depth, plus contrast behind the white prints precisely where a uniform near-white
            // ground left them relying on their hairline alone. The flat colour stays painted
            // underneath it (see the token's note).
            : 'relative -mx-4 overflow-hidden bg-gallery-cream bg-gallery-warmth border-b border-black/5 min-h-[140px] sm:min-h-[176px] flex items-center',
        )}
      >
        {/* A mock picture package on the right — an 8x10, two 5x7s and a block of wallets
            fanned out — instead of the single floating cutout this header used to show. It
            answers "what am I actually buying?" at a glance, and it fills from the customer's
            own favorites, so hearting a photo puts it straight onto the 8x10.

            Backgrounds ARE composited here (see packageSheets), reversing the old header's
            deliberate bare-cutout choice: a printed sheet has a background, and showing the
            print without one would defeat the point.

            Save-deterrence for these photos is NOT here — it is on the gallery root, once,
            for every image on the page. See the comment there. No gradient fade either: the
            sheets are discrete white-bordered objects on the navy, so there is no hard image
            edge left to hide, and a fade would only dim the front sheet. */}
        {!senior && bannerPose && (
          // Inset from the right edge rather than flush against it: the sheets are objects
          // sitting on the banner, and an object touching the edge reads as cropped.
          //
          // The design box is a fixed 260x176 and is SCALED rather than re-laid-out at each
          // breakpoint: one set of fan coordinates, and no `hidden sm:block` (which
          // Bootstrap's `.hidden { display:none !important }` would win outright).
          //
          // Three scale steps, not two, and the middle one earns its keep: at `sm` the banner
          // is only 640 wide, and a full-size 260px fan plus the right inset would run under
          // the headline. Absolute positioning (not a flex child) is deliberate too —
          // `transform: scale` does not shrink the LAYOUT box, so a 176px-tall flex child would
          // force the phone banner past its 140px height.
          //
          // Sized to whatever the full-size type leaves, measured per breakpoint against the
          // text column plus the right inset:
          //   phone 390:  54% text = 211px, 16px inset -> 163px left  -> 0.62 (260 * 0.62 = 161)
          //   sm    640:  56% text = 358px, 32px inset -> 250px left  -> 0.88 (229) fits easily
          //   md    768+: 58% text = 445px, 32px inset -> 291px left  -> full size (260)
          // Still better than the 0.6/0.78 it started at, especially at sm, where the old value
          // was leaving 60px of the available width unused.
          <div className="absolute inset-y-0 right-4 sm:right-8 z-0 flex items-center">
            <PackageMockup
              sheets={packageSheets}
              className="scale-[0.62] sm:scale-[0.88] md:scale-100"
            />
          </div>
        )}

        <div
          className={cn(
            senior
              ? 'order-last sm:order-2 basis-full sm:basis-auto text-center'
              // Left-justified, and capped short of the photo so a long name never runs
              // under the child's face. More room on the LEFT than the gallery's own px-4
              // gutter: text starting flush against a full-bleed edge reads as an overflow
              // rather than a margin.
              // 54/56/58%. It was briefly narrowed to 44/48/52% to buy the package room, but
              // that only worked with the smaller kicker type, and the type is back at full
              // size — a full-size headline in a 44% column wraps to four lines on a phone and
              // pushes the banner past its own height.
              : 'relative z-[2] text-left pl-6 pr-4 py-6 sm:pl-10 sm:py-8 w-[54%] sm:w-[56%] md:w-[58%]',
          )}
        >
          {/* Two weights at ONE size when the caller supplies a lead: the child's name is what
              a parent is here for, the grade is context, so the name carries the weight and the
              rest recedes into a warm grey. A size down from the all-bold version — the same
              4xl in a light grey would shout the context louder than the name.

              A smaller, uppercase, letterspaced KICKER was tried here for the grade. It made a
              tidier hierarchy on paper and read as too small on the page: at this width the
              grade dropped to 10-12px, which is caption size for a line a parent is meant to
              take in at a glance. Reverted deliberately — do not reintroduce it without
              looking at it on a phone first.

              text-balance matters either way: this headline always wraps on a phone, and left
              alone it breaks wherever the box runs out — a long line above a stub. */}
          <h1
            className={cn(
              'tracking-tight leading-[1.08] text-balance',
              headlineLead
                ? 'text-2xl md:text-3xl font-medium'
                : 'text-3xl md:text-4xl font-extrabold',
              // eslint-disable-next-line no-nested-ternary
              senior
                ? (headlineLead ? 'text-gray-500' : 'text-gray-900')
                // The GRADE (and any flat headline) is the dark one now — warm charcoal, which
                // on an ivory ground reads as black without the coldness a true #000 brings.
                // The name carries the colour instead; see below.
                : 'text-gallery-ink',
            )}
          >
            {headlineLead && (
              <>
                {/* The child's NAME in brand blue, and it is the only blue in the header — the
                    subhead gave the colour up so this keeps it. It is the full #008fcf rather
                    than the muted #3A91C2 the subhead used, because a name is worth the
                    saturation and this is now the one place carrying it. */}
                <span className={cn('font-extrabold', senior ? 'text-gray-900' : 'text-shoob-blue')}>
                  {headlineLead}
                </span>
                {' '}
              </>
            )}
            {headlineText}
          </h1>

          {/* Subheading. A warm grey, NOT blue: the brand blue moved up to the child's name,
              and two blues stacked three lines apart competed instead of leading the eye. This
              line is the quietest of the three by design — its job is warmth, not information.
              The senior card keeps the brand blue it has on its white ground.

              Caller-supplied rather than hardcoded — the wording is audience-specific, and
              this component owns layout, not copy (same contract as `headline`). Audiences
              that pass none, seniors included, render no subheading at all. */}
          {subhead && (
            <p
              className={cn(
                'mt-1.5 text-sm md:text-base font-medium',
                senior ? 'text-shoob-blue' : 'text-gallery-muted',
              )}
            >
              {subhead}
            </p>
          )}
        </div>
      </div>

      {/* Step 1 banner: choosing the yearbook pose is a required, one-time decision, so it
          keeps the blue box. Step 2 does not -- see below. The bulk actions (Favorite All /
          Clear) live in the sticky bottom bar as real buttons. */}
      {step === 1 && (
        <div className="mt-2 bg-shoob-blue text-white rounded-md px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20">
                <CapIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-bold text-lg leading-tight">Step 1: Select your Yearbook Pose</p>
                <p className="text-sm text-white/90 flex items-center gap-1">
                  Tap the
                  {' '}
                  <CapIcon className="h-4 w-4 inline" />
                  {' '}
                  on the photo you want in the yearbook
                </p>
              </div>
            </div>
            {yearbook && (
              <span className="shrink-0 rounded-full bg-white/20 px-4 py-1 text-sm font-medium">Required</span>
            )}
          </div>
        </div>
      )}

      {/* Step 2. Seniors keep `main`'s blue banner (white text in a shoob-blue box, with the
          "Yearbook Pose Selected" line and the pink-circle heart). Everyone else gets a plain
          instruction: on those pages the photos ARE the content, and a full-width blue bar
          competed with them for the eye. */}
      {step === 2 && senior && (
        <div className="mt-2 bg-shoob-blue text-white rounded-md px-4 py-3">
          {yearbook && (
            <div className="flex items-center justify-between gap-3">
              {/* Done, so it recedes -- muted, one line across the box. */}
              <p className="text-base leading-tight text-white/80 flex items-center gap-2 min-w-0">
                <CapIcon className="h-4 w-4 shrink-0" />
                Yearbook Pose Selected
              </p>
              <span className="shrink-0 rounded-full bg-white/20 px-3 py-1 text-xs font-medium flex items-center gap-1">
                <CapIcon className="h-3.5 w-3.5" />
                {' '}
                to change
              </span>
            </div>
          )}
          <p className={cn('text-xl leading-tight font-bold flex items-center gap-2.5', yearbook && 'mt-2')}>
            {/* Pink-circle heart matching the favorite button on each photo card, so the
                instruction visually points at the control the customer taps. */}
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pink-500 shadow">
              <HeartIcon filled className="h-4 w-4 text-white" />
            </span>
            {/* Nudged down a hair so the cap-height text optically centers on the circle. */}
            <span className="relative top-[1px]">Select your favorites.</span>
          </p>
        </div>
      )}

      {step === 2 && !senior && (
        <div className="mt-4">
          {yearbook && (
            <div className="flex items-center justify-between gap-3 text-gray-500">
              {/* Done, so it recedes -- muted, one line above the instruction. */}
              <p className="text-sm leading-tight flex items-center gap-2 min-w-0">
                <CapIcon className="h-4 w-4 shrink-0" />
                Yearbook Pose Selected
              </p>
              <span className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium flex items-center gap-1">
                <CapIcon className="h-3.5 w-3.5" />
                {' '}
                to change
              </span>
            </div>
          )}
          {/* Instruction and Favorite All on ONE line, the action right-justified. The
              sticky bar is below the fold on a tall grid, so a parent who wants everything
              had to scroll to find out they could say so in one tap. */}
          <div className={cn('flex items-center justify-between gap-3', yearbook && 'mt-2')}>
            <p className="text-base leading-tight font-bold text-gray-900">
              Select your favorites.
            </p>
            <button
              type="button"
              onClick={bulkAction.onClick}
              className={cn(quietButtonClass, 'shrink-0 min-h-[44px] px-4 py-2 text-sm hover:bg-gray-50')}
            >
              <FavoriteIcon filled={bulkAction.filled} className="h-4 w-4" />
              {bulkAction.label}
            </button>
          </div>
        </div>
      )}

      {/* Why some photos can't be the yearbook pick — only while one is being chosen. */}
      {step === 1 && yearbook?.note && (
        <p className="mt-3 text-sm text-gray-600">{yearbook.note}</p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-5">
        {/* Upsell tile (elementary "Download All My Photos"). It leads the grid, as it does
            today — it is a real revenue path, not decoration, so the gallery carries it
            rather than losing it in the redesign. Audiences without one pass no `promo`. */}
        {promo && (
          // The promo is a real product ("Download All My Photos"), so it sits in the grid as a
          // single card exactly like the pose cards -- one column on phones, one of four on
          // desktop -- rather than a full-width banner.
          //
          // NOT one big button: only the BUY NOW strip at the bottom triggers the purchase, so
          // tapping the ad or dragging to scroll past it never adds the package to the cart.
          //
          // Built to the SAME shape as a pose card for everyone but seniors: a 4:5 panel plus
          // the caption row, so the tile stands exactly as tall and the row of cards reads as
          // one set. It used to be a 3:4 card with no row, which made it 1.333w tall against
          // the pose cards' 1.25w + 48px -- about 26px short at a 260px column, and visibly
          // out of line. The row renders EMPTY here: no frame ID and no heart, because there is
          // no pose to identify and nothing to favorite.
          //
          // Seniors keep `main`'s 3:4 rounded-2xl tile untouched.
          <div className={cn(
            'relative bg-white',
            senior
              ? 'overflow-hidden aspect-[3/4] rounded-2xl shadow-sm'
              : 'rounded-b-lg shadow-[0_4px_10px_rgba(0,0,0,0.18)]',
          )}
          >
            {/* Panel. Square top corners running edge to edge into the card, exactly as the
                pose cards' photo does. */}
            <div className={cn(senior ? 'h-full w-full' : 'relative overflow-hidden aspect-[4/5]')}>
              {/* `card` (a whole rendered tile, e.g. BuyAllCard with its own price strip and
                  button) replaces the bare image + invisible BUY NOW strip. Issue #5218. */}
              {promo.card || (
                <>
                  <img src={promo.imageUrl} alt={promo.label} className="block w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={promo.onClick}
                    aria-label={promo.label}
                    className="absolute inset-x-0 bottom-0 h-[20%] bg-transparent"
                  />
                </>
              )}
            </div>

            {!senior && <div className={CAPTION_ROW} />}
          </div>
        )}

        {orderedPoses.map((pose) => {
          // Two designs, picked by audience: seniors render `main`'s card untouched, every
          // other pricelist gets the redesigned one. See SeniorPoseCard's note above.
          const Card = senior ? SeniorPoseCard : PoseCard;

          return (
            <Card
              key={pose.id}
              pose={pose}
              // Caller-supplied backdrop (seniors: the school's senior background) when
              // there is one; otherwise this pose's background per the pricelist's
              // package-poses spec — so a cutout is never rendered bare.
              backgroundUrl={backdropUrl || getAppropriateBg(packagePoses, pose, backgrounds, pricelist)?.imageUrl}
              isYb={!!yearbookId && pose.id === yearbookId}
              pending={pendingYearbookId === pose.id}
              isFav={favorites.has(pose.id)}
              ineligible={!isEligible(pose)}
              step={step}
              yearbook={yearbook}
              onZoom={() => setZoomedPoseId(pose.id)}
              onSelectYearbook={() => selectYearbook(pose)}
              onToggleFavorite={() => toggleFavorite(pose.id)}
            />
          );
        })}
      </div>

      {/* Enlarged view. The same composite the card shows, plus the pricelist's graphics
          overlay (getAppropriateForeground) — so what they're judging is what prints.
          Sized to ~1.5x a card (not the whole screen), and clicking anywhere — the photo,
          the backdrop, the X, or Escape — closes it. */}
      {zoomedPose && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Photo ${poseNumberOf(zoomedPose)}`}
        >
          {/* Backdrop — its own button so clicking outside the photo closes. bg-transparent
              is load-bearing: preflight is off, so a bare <button> would paint its default
              light-gray buttonface over the dim and wash the backdrop out. */}
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 cursor-zoom-out bg-transparent"
            onClick={() => setZoomedPoseId(null)}
          />

          <div className="relative z-10 w-[420px] max-w-[90vw]">
            {/* X — always visible in the corner, so closing never depends on scrolling to a
                button below a tall photo (the bug this replaces). */}
            <button
              type="button"
              aria-label="Close"
              onClick={() => setZoomedPoseId(null)}
              className="absolute -top-3 -right-3 z-20 h-11 w-11 rounded-full bg-white text-gray-800 shadow-lg flex items-center justify-center text-lg leading-none"
            >
              ✕
            </button>

            {/* Everyone but seniors gets the grid's card, one size up: the photo on a white
                surface with the frame ID and the heart in a caption row attached underneath,
                so the enlarged view is recognisably the same object the customer tapped.
                Seniors keep `main`'s loose photo + floating pill row (see SeniorPoseCard). */}
            <div className={cn(!senior && 'bg-white rounded-b-lg shadow-[0_4px_10px_rgba(0,0,0,0.18)]')}>
              {/* The photo is itself a close button — clicking it dismisses the viewer. */}
              <button
                type="button"
                aria-label="Close enlarged photo"
                onClick={() => setZoomedPoseId(null)}
                className="block w-full p-0 cursor-zoom-out"
              >
                <div
                  className={cn(
                    'relative overflow-hidden',
                    senior ? 'bg-neutral-900 rounded-2xl' : 'bg-white',
                    // Seniors stay on the frozen 4:5; everyone else takes the photo's own
                    // ratio once it's known, and 4:5 until then.
                    (senior || !zoomRatio) && 'aspect-[4/5]',
                  )}
                  style={!senior && zoomRatio ? { aspectRatio: zoomRatio } : undefined}
                >
                  <PreviewImage
                    foregroundUrl={zoomedPose.watermarkUrl}
                    backgroundUrl={backdropUrl
                      || getAppropriateBg(packagePoses, zoomedPose, backgrounds, pricelist)?.imageUrl}
                    actualForegroundUrl={getAppropriateForeground(packagePoses, zoomedPose, pricelist)?.imageUrl}
                    containerClass="relative z-[1] w-full h-full overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:object-contain"
                  />
                </div>
              </button>

              {/* Caption row — the card's own, so the frame ID and the heart sit exactly
                  where they do in the grid. Outside the photo button, so favoriting from
                  the enlarged view doesn't close it. */}
              {!senior && (
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <span
                    className="text-sm font-semibold tabular-nums text-gray-400"
                    title={`Pose ${poseNumberOf(zoomedPose)}${cameraLetterOf(zoomedPose) ? `, Camera ${cameraLetterOf(zoomedPose)}` : ''}`}
                  >
                    {`${poseNumberOf(zoomedPose)}${cameraLetterOf(zoomedPose)}`}
                  </span>

                  <button
                    type="button"
                    onClick={() => toggleFavorite(zoomedPose.id)}
                    aria-label={favorites.has(zoomedPose.id) ? 'Remove favorite' : 'Add favorite'}
                    className={cn(
                      'h-11 w-11 rounded-full flex items-center justify-center bg-transparent',
                      favorites.has(zoomedPose.id) ? 'text-pink-500' : 'text-gray-400 hover:text-pink-500',
                    )}
                  >
                    {/* Matches the grid card's heart — this row IS that card, one size up. */}
                    <FavoriteIcon filled={favorites.has(zoomedPose.id)} className="h-[22px] w-[22px]" />
                  </button>
                </div>
              )}
            </div>

            {/* Frame ID + favorite. Separate from the photo button, so favoriting from the
                zoom doesn't close it. */}
            {senior && (
            <div className="mt-3 flex items-center justify-between gap-4">
              <span className="rounded bg-black/60 px-2 py-1 text-sm font-semibold tabular-nums text-white">
                {`${poseNumberOf(zoomedPose)}${cameraLetterOf(zoomedPose)}`}
              </span>

              <button
                type="button"
                onClick={() => toggleFavorite(zoomedPose.id)}
                className={cn(
                  'flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold shadow',
                  favorites.has(zoomedPose.id)
                    ? 'bg-pink-500 text-white'
                    : 'bg-white text-gray-900 hover:bg-gray-100',
                )}
              >
                <HeartIcon filled={favorites.has(zoomedPose.id)} className="h-4 w-4" />
                {favorites.has(zoomedPose.id) ? 'Favorited' : 'Favorite'}
              </button>
            </div>
            )}

            {/* Pager — BELOW the card, not floating on the photograph. Overlaid arrows sat
                inside the picture and read as part of it however transparent they were made;
                down here on the dim backdrop there is no question they are controls.

                Every audience, seniors included: the senior card design is frozen, but
                flipping between photos is an interaction rather than a card style, and
                comparing shots without closing back to the grid each time is as useful to a
                senior as to a parent. Disabled at the ends rather than wrapping. */}
            <div className="mt-3 flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => stepZoom(-1)}
                disabled={!hasPrev}
                aria-label="Previous photo"
                className={cn(arrowClass, hasPrev ? 'hover:bg-white/75' : 'opacity-30 cursor-not-allowed')}
              >
                <ChevronIcon direction="left" className="h-5 w-5" />
              </button>

              {/* Position counter between the arrows — the standard lightbox idiom, and what
                  gives a centred pair something to sit either side of. It also answers "how
                  many more are there?", which is the question the arrows raise.

                  tabular-nums + a min width so the arrows don't jog sideways when the count
                  ticks from 9 to 10. */}
              <span className="min-w-[4.5rem] text-center text-sm font-medium tabular-nums text-white/80">
                {`${zoomedIndex + 1} of ${orderedPoses.length}`}
              </span>

              <button
                type="button"
                onClick={() => stepZoom(1)}
                disabled={!hasNext}
                aria-label="Next photo"
                className={cn(arrowClass, hasNext ? 'hover:bg-white/75' : 'opacity-30 cursor-not-allowed')}
              >
                <ChevronIcon direction="right" className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky bar, ONE row at every width (designer, 07-23). On desktop the buttons shrink
          to their content and right-align; padding-bottom clears the home indicator.

          Two buttons, not three: the bulk toggle (Favorite All / Clear Favorites) and the
          continue CTA, which share the space (flex-1). Continue is the only FILLED button, so
          the row says what to do next rather than offering equally loud choices.

          Seniors keep `main`'s three all-blue pills. */}
      {step === 2 && (
        <div
          className={cn(
            'fixed bottom-0 inset-x-0 z-20 bg-white border-t border-gray-200 px-4 pt-3',
            // Shadow cast UPWARD (negative y), because the content this bar floats over is
            // above it — Tailwind's stock shadow-* all cast down and would be invisible
            // here, hidden behind the bar. Paired with the hairline border: the border is
            // the crisp edge, the shadow is the lift. Seniors keep `main`'s flat bar.
            !senior && 'shadow-[0_-4px_16px_rgba(0,0,0,0.08)]',
          )}
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          {/* Why Continue did nothing. Sits directly above the button that was just pressed,
              so the answer is where the eye already is. Deliberately not red: nothing has
              gone wrong, the parent simply has not picked yet — and the pink heart points at
              the control that fixes it rather than telling them off. role=status +
              aria-live=polite so a screen reader hears it without stealing focus. */}
          {continueHint && !hasFavorites && (
            <div
              role="status"
              aria-live="polite"
              className="mx-auto max-w-6xl mb-2 flex items-center justify-center gap-2 rounded-lg bg-gray-50 ring-1 ring-gray-200 px-3 py-2 text-sm text-gray-700"
            >
              <FavoriteIcon filled className="h-4 w-4 shrink-0 text-pink-500" />
              <span>
                Tap the heart on a photo to pick a favorite — or
                {' '}
                <strong className="font-semibold">Favorite All</strong>
                {' '}
                — to continue.
              </span>
            </div>
          )}

          <div className="mx-auto max-w-6xl flex flex-row items-stretch justify-center sm:justify-end gap-2 sm:gap-3">
            {/* Seniors keep `main`'s pair of solid Shoob-blue pills verbatim — the senior
                favorites screen is frozen on its production design (see SeniorPoseCard).
                Delete this block the day seniors adopt the redesign. */}
            {senior ? (
              <>
                <button
                  type="button"
                  onClick={favoriteAll}
                  disabled={allFavorited}
                  className={cn(
                    'flex-1 sm:flex-none sm:w-auto rounded-full px-3 sm:px-6 py-3 text-sm sm:text-base font-semibold text-white bg-shoob-blue flex items-center justify-center gap-2 shadow whitespace-nowrap',
                    allFavorited ? 'opacity-50 cursor-not-allowed' : 'hover:bg-shoob-blueDark',
                  )}
                >
                  Favorite All
                </button>
                <button
                  type="button"
                  onClick={clearFavorites}
                  disabled={!hasFavorites}
                  className={cn(
                    'flex-none sm:w-auto rounded-full px-4 sm:px-6 py-3 text-sm sm:text-base font-semibold text-white bg-shoob-blue flex items-center justify-center gap-2 shadow',
                    !hasFavorites ? 'opacity-50 cursor-not-allowed' : 'hover:bg-shoob-blueDark',
                  )}
                >
                  Clear
                </button>
              </>
            ) : (
              // The single bulk toggle. Same object the header button renders, so the two
              // copies cannot say different things.
              <button
                type="button"
                onClick={bulkAction.onClick}
                className={cn(
                  quietButtonClass,
                  stickyButtonSize,
                  'hover:bg-gray-50',
                )}
              >
                <FavoriteIcon filled={bulkAction.filled} className="h-4 w-4" />
                {bulkAction.label}
              </button>
            )}
            {/* Continue. Grey while there is nothing to continue WITH, Shoob blue the moment
                there is — the colour change is the feedback that the first heart counted.
                Solid grey rather than a faded blue: a washed-out version of the live colour
                reads as "broken button", where grey reads as "not yet".

                Grey but PRESSABLE (aria-disabled, not disabled): a real `disabled` button
                emits no click, so it can never explain itself. Pressing it with nothing
                chosen raises the hint above instead of doing nothing at all. Seniors keep
                the genuinely-disabled faded blue they have in production. */}
            <button
              type="button"
              onClick={() => {
                if (!hasFavorites) {
                  setContinueHint(true);
                  return;
                }
                onContinue([...favorites]);
              }}
              disabled={senior && !hasFavorites}
              aria-disabled={!hasFavorites}
              className={cn(
                'rounded-full font-semibold text-white flex items-center justify-center gap-1.5 shadow text-center leading-tight',
                senior
                  ? 'flex-1 sm:flex-none sm:w-auto px-3 sm:px-6 py-3 text-sm sm:text-base'
                  : stickyButtonSize,
                // eslint-disable-next-line no-nested-ternary
                hasFavorites
                  ? 'bg-shoob-blue hover:bg-shoob-blueDark'
                  : senior ? 'bg-shoob-blue opacity-50 cursor-not-allowed' : 'bg-gray-500',
              )}
            >
              {continueLabel}
              {hasFavorites && <span aria-hidden="true">→</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

PortraitsGallery.defaultProps = {
  poses: [],
  backgrounds: [],
  subhead: null,
  backdropUrl: null,
  yearbook: null,
  favoritesKey: null,
  promo: null,
  onContinue: () => {},
  continueLabel: 'Build Your Package',
  senior: false,
};

PortraitsGallery.propTypes = {
  // Spraypaint Pose models — objects, not a fixed shape.
  // eslint-disable-next-line react/forbid-prop-types
  poses: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  backgrounds: PropTypes.arrayOf(PropTypes.object),
  packageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // A string renders flat and all-bold. `{ lead, rest }` renders on one line in two
  // weights — `lead` extrabold and dark, `rest` lighter and grey.
  headline: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ lead: PropTypes.string.isRequired, rest: PropTypes.string.isRequired }),
  ]).isRequired,
  // Short line UNDER the headline (e.g. "Another year to remember"). Omit it and none renders.
  subhead: PropTypes.string,
  // Backdrop for every card, overriding the per-pose package background. Seniors are shot
  // as cutouts and printed on the school's senior background, so they pass it; audiences
  // whose backgrounds come from the pricelist pass nothing.
  backdropUrl: PropTypes.string,
  // The yearbook-pick capability. Omit it and the gallery is favorites-only: no cap
  // buttons, no eligibility gate, no step 1.
  yearbook: PropTypes.shape({
    selectedPoseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // (pose) => bool. Fail-open on an unclassified pose.
    isEligible: PropTypes.func.isRequired,
    // (pose) => 0|1|2. Optional 3-way ordering band; falls back to isEligible when absent.
    bandOf: PropTypes.func,
    // (pose) => Promise<bool>. Persists the choice; resolve false and nothing is favorited.
    onSelect: PropTypes.func.isRequired,
    // Shown while choosing, and as the blocked button's tooltip.
    note: PropTypes.string,
  }),
  // sessionStorage key for the favorites, so they survive into the package builder.
  favoritesKey: PropTypes.string,
  // An upsell tile at the head of the grid (elementary "Download All My Photos").
  promo: PropTypes.shape({
    imageUrl: PropTypes.string,
    label: PropTypes.string,
    onClick: PropTypes.func,
    // A fully rendered tile (BuyAllCard) in place of imageUrl + the BUY NOW strip.
    card: PropTypes.node,
  }),
  onContinue: PropTypes.func,
  continueLabel: PropTypes.string,
  // Renders the senior flow's original design -- header block, blue step banner, and
  // pose cards exactly as they are on `main`. Everyone else gets the redesigned cards.
  // Passed by SeniorPortraitsGallery only.
  senior: PropTypes.bool,
};
